import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Download, Eye, XCircle, CheckCircle, RefreshCw, ChevronDown, ChevronUp, Layers, Calendar, Square, CheckSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useBooking } from '../contexts/BookingContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { consolidatePlayoBookings, formatDuration, isConsolidatedPlayoBooking, getOriginalBookings } from '../utils/playoConsolidation';
import { formatTime, toLocalYMD } from '../utils/timeSlots';
import MonthlyPaymentsTab from './MonthlyPaymentsTab';
import BulkCancelModal from './BulkCancelModal';
import BookingDialog from './BookingDialog';

// Date helper functions
// NOTE: We format Dates via LOCAL components (getFullYear/getMonth/getDate)
// and NEVER via `toISOString().split('T')[0]`, which rolls back a day for
// admins in IST (UTC+5:30) between 00:00 and 05:30 IST. That was the root
// cause of the "This Month starts on the 31st of previous month" bug.
const getToday = () => toLocalYMD(new Date());

const getDateRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (preset) {
    case 'today':
      return { start: getToday(), end: getToday() };
    case 'thisWeek': {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // Saturday
      return {
        start: toLocalYMD(startOfWeek),
        end: toLocalYMD(endOfWeek),
      };
    }
    case 'thisMonth': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return {
        start: toLocalYMD(startOfMonth),
        end: toLocalYMD(endOfMonth),
      };
    }
    case 'lastMonth': {
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        start: toLocalYMD(startOfLastMonth),
        end: toLocalYMD(endOfLastMonth),
      };
    }
    default:
      return { start: '', end: '' };
  }
};

// Helper functions
// Compact time format for mobile
const formatTimeCompact = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'P' : 'A';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minutes}${ampm}`;
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'Asia/Kolkata'
  });
};

// Compact date format for mobile
const formatDateCompact = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Kolkata'
  });
};

const BookingManagement = () => {
  const { bookings, fetchBookings, updateBooking, cancelBooking, loading, bookingsTotal, bookingsTruncated } = useBooking();
  const { canEditBookings } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [bookingTypeFilter, setBookingTypeFilter] = useState('all');
  const [timeCategory, setTimeCategory] = useState('all');
  const [expandedPlayoBookings, setExpandedPlayoBookings] = useState({});
  
  // Enhanced date filtering
  const [datePreset, setDatePreset] = useState('today'); // 'today', 'thisWeek', 'thisMonth', 'lastMonth', 'custom', 'all'
  const [startDate, setStartDate] = useState(getToday());
  const [endDate, setEndDate] = useState(getToday());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [view, setView] = useState('bookings'); // 'bookings' | 'monthly'

  // Bulk-cancel state
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [viewBooking, setViewBooking] = useState(null);

  // Update date range when preset changes
  useEffect(() => {
    if (datePreset !== 'custom' && datePreset !== 'all') {
      const range = getDateRange(datePreset);
      setStartDate(range.start);
      setEndDate(range.end);
    }
  }, [datePreset]);

  // Single source of truth for fetching. This runs on mount (defaults are
  // set), on preset change (via startDate/endDate updates), and on any
  // manual filter change. An earlier unconditional mount-fetch was removed
  // because it fired in parallel with this one and, being unfiltered,
  // occasionally resolved LAST and overwrote the filtered results — that
  // was the exact "sometimes empty, sometimes all" race admins hit.
  useEffect(() => {
    if (startDate && endDate) {
      fetchBookings({ start_date: startDate, end_date: endDate });
    } else if (datePreset === 'all') {
      fetchBookings();
    }
  }, [startDate, endDate, datePreset, fetchBookings]);

  // Consolidate Playo bookings for better UX
  const { consolidated: consolidatedBookings, playoGroups } = useMemo(() => {
    return consolidatePlayoBookings(bookings);
  }, [bookings]);

  // Toggle expansion of a consolidated Playo booking
  const togglePlayoExpansion = (bookingId) => {
    setExpandedPlayoBookings(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  // Check if booking date is within selected range
  const isDateInRange = (bookingDate) => {
    if (datePreset === 'all') return true;
    if (!startDate || !endDate) return true;
    return bookingDate >= startDate && bookingDate <= endDate;
  };

  // Peak hour check
  const isBookingPeak = (booking) => {
    const time = booking.start_time || booking.startTime;
    if (!time) return false;
    const hour = parseInt(time.split(':')[0]);
    return (hour >= 6 && hour < 9) || (hour >= 17 && hour < 22);
  };

  const filteredBookings = consolidatedBookings.filter((booking) => {
    const customerName = booking.customer_name || booking.customerName || '';
    const customerEmail = booking.customer_email || booking.customerEmail || '';
    const customerPhone = booking.customer_phone || booking.customerPhone || '';
    const bookingId = booking.id || '';
    
    const matchesSearch =
      customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customerEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customerPhone.includes(searchQuery) ||
      bookingId.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    const matchesDateRange = isDateInRange(booking.date);
    const matchesSource = sourceFilter === 'all' || booking.source === sourceFilter;
    
    const bType = (booking.booking_type || booking.bookingType || 'b2c').toLowerCase();
    const matchesBookingType = bookingTypeFilter === 'all' || bType === bookingTypeFilter;
    
    const matchesTimeCategory = timeCategory === 'all' || 
      (timeCategory === 'peak' && isBookingPeak(booking)) ||
      (timeCategory === 'non-peak' && !isBookingPeak(booking));

    return matchesSearch && matchesStatus && matchesDateRange && matchesSource && matchesBookingType && matchesTimeCategory;
  });

  const handleConfirm = async (id) => {
    if (!canEditBookings) return;
    try {
      await updateBooking(id, { status: 'confirmed' });
      toast({
        title: "Booking Confirmed",
        description: "The booking has been confirmed successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleCancel = async (id) => {
    if (!canEditBookings) return;
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      try {
        await cancelBooking(id);
        toast({
          title: "Booking Cancelled",
          description: "The booking has been cancelled.",
          variant: "destructive"
        });
      } catch (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  };

  // Calculate duration in minutes from start and end time
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    let startMins = startH * 60 + startM;
    let endMins = endH * 60 + endM;
    // Handle overnight bookings
    if (endMins <= startMins) endMins += 24 * 60;
    return endMins - startMins;
  };

  const exportBookings = () => {
    const headers = [
      'Booking ID',
      'Date',
      'Start Time',
      'End Time',
      'Duration (mins)',
      'Customer Name',
      'Phone',
      'Email',
      'Court/Slot',
      'Sport',
      'Source',
      'Booking Type',
      'Amount',
      'Amount Paid',
      'Pending Amount',
      'Payment Status',
      'Payment Method',
      'Collected By',
      'Razorpay Payment ID 1',
      'Razorpay Amount 1',
      'Payment Date 1',
      'Razorpay Order ID 1',
      'UPI RRN 1',
      'Razorpay Payment ID 2',
      'Razorpay Amount 2',
      'Payment Date 2',
      'Razorpay Order ID 2',
      'UPI RRN 2',
      'Offline Cash Amount',
      'Cash Collected By',
      'Cash Collection Date',
      'Offline UPI Amount',
      'UPI Collected By',
      'UPI Collection Date',
      'Payment Trail Note',
      'Trail Reconciles?',
      'Booking Status',
      'Refund Amount',
      'Refund Status',
      'Refund Date',
      'Razorpay Refund ID'
    ];

    const rows = filteredBookings.map(b => {
      const startTime = b.start_time || b.startTime || '';
      const endTime = b.end_time || b.endTime || '';
      const amount = b.amount || 0;
      const amountPaid = b.amount_paid || b.amountPaid || 0;
      const pendingAmount = amount - amountPaid;

      // Itemized payment trail from the ledger: ONLINE=Razorpay (has ref); OFFLINE=cash/upi.
      const allocs = (b.payment_allocations || b.paymentAllocations || []).filter(
        a => a && a.type !== 'refund' && ((a.court_allocated != null ? a.court_allocated : a.total_amount) || 0) > 0
      );
      const fmtDt = (iso) => {
        if (!iso) return '';
        try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
        catch { return ''; }
      };
      const rzp = [], cashArr = [], upiArr = [], otherArr = [];
      allocs.forEach(a => {
        const amt = a.court_allocated != null ? a.court_allocated : (a.total_amount || 0);
        const pm = (a.payment_method || '').toLowerCase();
        const by = a.collected_by_name || a.collected_by || '';
        const dt = fmtDt(a.event_at);
        if (a.razorpay_payment_id || pm === 'online') {
          rzp.push([
            a.razorpay_payment_id || '',
            amt,
            dt,
            a.razorpay_order_id || '',
            a.razorpay_bank_rrn || '',
          ]);
        } else if (pm === 'cash' || pm === 'cash_offline') cashArr.push([amt, by, dt]);
        else if (pm === 'upi') upiArr.push([amt, by, dt]);
        else otherArr.push([amt, by, dt]);
      });
      // Legacy fallback: paid money but no ledger entries.
      if (allocs.length === 0 && amountPaid > 0) {
        const pm = (b.payment_method || b.paymentMethod || '').toLowerCase();
        const by = b.collected_by_name || b.collected_by || '';
        const dt = fmtDt(b.updated_at || b.created_at);
        if ((b.razorpay_payment_id || b.razorpayPaymentId) || pm === 'online') {
          rzp.push([
            b.razorpay_payment_id || b.razorpayPaymentId || '',
            amountPaid,
            dt,
            b.razorpay_order_id || '',
            b.razorpay_bank_rrn || '',
          ]);
        } else if (pm === 'cash' || pm === 'cash_offline') cashArr.push([amountPaid, by, dt]);
        else if (pm === 'upi') upiArr.push([amountPaid, by, dt]);
        else otherArr.push([amountPaid, by, dt]);
      }
      const notes = [];
      if (rzp.length > 2) notes.push(`${rzp.length} Razorpay payments; showing first 2, extra ₹${rzp.slice(2).reduce((s, x) => s + x[1], 0)}`);
      if (otherArr.length) notes.push(`Other offline ₹${otherArr.reduce((s, x) => s + x[0], 0)}`);
      const r1 = rzp[0] || ['', '', '', '', ''];
      const r2 = rzp[1] || ['', '', '', '', ''];
      const cashAmt = cashArr.length ? cashArr.reduce((s, x) => s + x[0], 0) : '';
      const cashBy = [...new Set(cashArr.map(x => x[1]).filter(Boolean))].join('; ');
      const cashDate = cashArr.length ? cashArr[cashArr.length - 1][2] : '';
      const upiAmt = upiArr.length ? upiArr.reduce((s, x) => s + x[0], 0) : '';
      const upiBy = [...new Set(upiArr.map(x => x[1]).filter(Boolean))].join('; ');
      const upiDate = upiArr.length ? upiArr[upiArr.length - 1][2] : '';

      // Refund details (surfaced when a cancellation issued a refund)
      const refundIds = (b.payment_allocations || b.paymentAllocations || [])
        .filter(a => a && a.type === 'refund' && a.razorpay_refund_id)
        .map(a => a.razorpay_refund_id)
        .join(' | ');
      const refundDate = b.refund_processed_at
        ? new Date(b.refund_processed_at).toLocaleString('en-IN')
        : '';

      // Human-friendly Payment Method value. The top-level payment_method on
      // a booking is stored in two SEPARATE cases today:
      //   (i) manual offline collect-payment writes 'cash' / 'upi' / 'other';
      //  (ii) legacy Razorpay captures wrote the wire method ('upi', 'card',
      //       'netbanking', 'wallet') via _record_razorpay_refs_on_booking_update
      //       — so a 'upi' value can mean either offline UPI OR online UPI-on-
      //       Razorpay. We disambiguate by presence of an online allocation (or
      //       a top-level razorpay_payment_id): if the booking took an online
      //       payment, render 'upi'/'card'/... as "Online … (Razorpay)".
      const rawMethod = (b.payment_method || b.paymentMethod || '').toString();
      const methodKey = rawMethod.toLowerCase();
      const hasOnlinePayment =
        !!(b.razorpay_payment_id || b.razorpayPaymentId) ||
        (b.payment_allocations || b.paymentAllocations || []).some(a => {
          if (!a || a.type === 'refund') return false;
          const pm = (a.payment_method || '').toLowerCase();
          return !!a.razorpay_payment_id || pm === 'online' || pm.startsWith('online_');
        });
      const OFFLINE_LABELS = { cash: 'Offline Cash', cash_offline: 'Offline Cash', upi: 'Offline UPI', other: 'Offline Other' };
      const ONLINE_LABELS = { upi: 'Online UPI (Razorpay)', card: 'Online Card (Razorpay)', netbanking: 'Online Netbanking (Razorpay)', wallet: 'Online Wallet (Razorpay)', emi: 'Online EMI (Razorpay)', online: 'Online (Razorpay)' };
      let paymentMethodLabel;
      if (methodKey === 'online' || methodKey.startsWith('online_')) {
        paymentMethodLabel = ONLINE_LABELS[methodKey.replace(/^online_/, '') || 'online'] || 'Online (Razorpay)';
      } else if (hasOnlinePayment && ONLINE_LABELS[methodKey]) {
        paymentMethodLabel = ONLINE_LABELS[methodKey];
      } else if (OFFLINE_LABELS[methodKey]) {
        paymentMethodLabel = OFFLINE_LABELS[methodKey];
      } else {
        paymentMethodLabel = rawMethod;
      }

      // Reconciliation flag: NO whenever the ledger is inconsistent, YES only
      // when everything lines up. Catches both:
      //   (a) trail sum ≠ amount_paid → allocation ledger missing/extra entries
      //   (b) amount_paid > booking amount (double-credit / overpayment) — the
      //       internal sum matches amount_paid but the booking is over-credited,
      //       so a pure trail-vs-paid YES would mask the real bug.
      // ₹1 tolerance for float rounding. "-" when nothing has been paid.
      const trailSum = [
        ...rzp.map(x => Number(x[1]) || 0),
        ...cashArr.map(x => Number(x[0]) || 0),
        ...upiArr.map(x => Number(x[0]) || 0),
        ...otherArr.map(x => Number(x[0]) || 0),
      ].reduce((s, v) => s + v, 0);
      const paid = Number(amountPaid) || 0;
      const total = Number(amount) || 0;
      let reconciles;
      if (paid === 0) reconciles = '-';
      else if (Math.abs(trailSum - paid) > 1) reconciles = 'NO';
      else if (total > 0 && paid > total + 1) reconciles = 'NO';
      else reconciles = 'YES';

      return [
        b.id,
        b.date,
        startTime,
        endTime,
        calculateDuration(startTime, endTime),
        b.customer_name || b.customerName || '',
        b.customer_phone || b.customerPhone || '',
        b.customer_email || b.customerEmail || '',
        b.court_name || b.court_id || b.section || b.slot_type || b.slotType || '',
        b.sport || '',
        b.source || 'direct',
        (b.booking_type || b.bookingType || 'b2c').toUpperCase(),
        amount,
        amountPaid,
        pendingAmount,
        b.payment_status || b.paymentStatus || 'pending',
        paymentMethodLabel,
        b.collected_by_name || b.collected_by || '',
        r1[0], r1[1], r1[2], r1[3], r1[4],
        r2[0], r2[1], r2[2], r2[3], r2[4],
        cashAmt, cashBy, cashDate,
        upiAmt, upiBy, upiDate,
        notes.join('; '),
        reconciles,
        b.status,
        b.refund_amount || 0,
        b.refund_status || '',
        refundDate,
        refundIds
      ];
    });

    // Prepend BOM for Excel UTF-8 support (so ₹/special chars don't garble in Excel on Windows)
    const csv = '\uFEFF' + [headers, ...rows].map(row =>
      row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    const dateStr = (startDate && endDate)
      ? (startDate === endDate ? startDate : `${startDate}_to_${endDate}`)
      : toLocalYMD(new Date());
    a.download = `bookings-${dateStr}.csv`;
    document.body.appendChild(a);
    a.click();
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 100);

    toast({
      title: "Export Successful",
      description: `${filteredBookings.length} bookings exported to CSV.`,
    });
  };

  const stats = {
    total: filteredBookings.length,
    confirmed: filteredBookings.filter(b => b.status === 'confirmed').length,
    pending: filteredBookings.filter(b => b.status === 'pending').length,
    cancelled: filteredBookings.filter(b => b.status === 'cancelled').length,
  };

  // ===== Bulk-cancel helpers =====
  const MAX_BULK = 50;
  const selectableBookings = filteredBookings.filter(
    (b) => b.status !== 'cancelled' && !isConsolidatedPlayoBooking(b)
  );
  const selectedBookings = filteredBookings.filter((b) => selectedIds.has(b.id));
  const allVisibleSelected =
    selectableBookings.length > 0 &&
    selectableBookings.every((b) => selectedIds.has(b.id));

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        if (next.size >= MAX_BULK) {
          toast({
            title: `Limit reached`,
            description: `You can select up to ${MAX_BULK} bookings at a time.`,
            variant: 'destructive',
          });
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        // Deselect all visible selectable
        selectableBookings.forEach((b) => next.delete(b.id));
      } else {
        // Select up to MAX_BULK
        for (const b of selectableBookings) {
          if (next.size >= MAX_BULK) break;
          next.add(b.id);
        }
        if (selectableBookings.length > MAX_BULK) {
          toast({
            title: `Selected first ${MAX_BULK}`,
            description: `Max ${MAX_BULK} bookings at a time. Use filters to narrow further.`,
          });
        }
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const onBulkComplete = () => {
    clearSelection();
    fetchBookings({ start_date: startDate, end_date: endDate });
  };


  return (
    <div className="p-2 sm:p-6 pt-14 lg:pt-6 pb-32 lg:pb-6 space-y-3 sm:space-y-6">
      {/* Top tab toggle */}
      <div className="flex gap-2" data-testid="booking-mgmt-tabs">
        <button
          type="button"
          onClick={() => setView('bookings')}
          className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
            view === 'bookings'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="tab-all-bookings"
        >
          All Bookings
        </button>
        <button
          type="button"
          onClick={() => setView('monthly')}
          className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
            view === 'monthly'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="tab-monthly-payments"
        >
          Monthly Payments
        </button>
      </div>

      {view === 'monthly' ? <MonthlyPaymentsTab /> : (
      <>
      {/* Stats - Compact on mobile */}
      <div className="grid grid-cols-4 gap-2 sm:gap-4">
        <Card className="p-2 sm:p-0">
          <CardContent className="p-2 sm:pt-6 sm:p-6">
            <div className="text-lg sm:text-2xl font-bold">{stats.total}</div>
            <p className="text-[10px] sm:text-sm text-gray-500">Total</p>
          </CardContent>
        </Card>
        <Card className="p-2 sm:p-0">
          <CardContent className="p-2 sm:pt-6 sm:p-6">
            <div className="text-lg sm:text-2xl font-bold text-blue-600">{stats.confirmed}</div>
            <p className="text-[10px] sm:text-sm text-gray-500">Confirmed</p>
          </CardContent>
        </Card>
        <Card className="p-2 sm:p-0">
          <CardContent className="p-2 sm:pt-6 sm:p-6">
            <div className="text-lg sm:text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <p className="text-[10px] sm:text-sm text-gray-500">Pending</p>
          </CardContent>
        </Card>
        <Card className="p-2 sm:p-0">
          <CardContent className="p-2 sm:pt-6 sm:p-6">
            <div className="text-lg sm:text-2xl font-bold text-red-600">{stats.cancelled}</div>
            <p className="text-[10px] sm:text-sm text-gray-500">Cancelled</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="p-3 sm:p-6">
          <div className="flex justify-between items-center">
            <CardTitle className="text-base sm:text-xl">Bookings</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportBookings} className="text-xs sm:text-sm">
                <Download className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">Export</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => fetchBookings()} disabled={loading}>
                <RefreshCw className={`h-3 w-3 sm:h-4 sm:w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-2 sm:p-6 pt-0">
          {/* Date Filter Pills */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {[
              { key: 'today', label: 'Today' },
              { key: 'thisWeek', label: 'This Week' },
              { key: 'thisMonth', label: 'This Month' },
              { key: 'lastMonth', label: 'Last Month' },
              { key: 'custom', label: 'Custom' },
              { key: 'all', label: 'All' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => {
                  setDatePreset(key);
                  if (key === 'custom') setShowDatePicker(true);
                }}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  datePreset === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          
          {/* Custom Date Range Picker */}
          {(datePreset === 'custom' || showDatePicker) && (
            <div className="flex flex-wrap items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  className="px-2 py-1 border rounded text-xs bg-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  className="px-2 py-1 border rounded text-xs bg-white"
                />
              </div>
              <button
                onClick={() => setShowDatePicker(false)}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          )}
          
          {/* Date Range Display */}
          {datePreset !== 'all' && (
            <div className="text-xs text-gray-500 mb-3">
              Showing bookings: {new Date(startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} 
              {startDate !== endDate && ` - ${new Date(endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
              <span className="ml-2 text-blue-600 font-medium">({filteredBookings.length} bookings)</span>
            </div>
          )}

          {/* Truncation backstop — should never fire in practice (admin
              fetch uses `limit=all` capped at the backend's 20000-row hard
              ceiling), but if a range ever exceeds it we surface it loudly
              instead of silently missing data. */}
          {bookingsTruncated && (
            <div
              className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded-lg px-3 py-2 mb-3"
              data-testid="bookings-truncation-banner"
            >
              ⚠ Showing first {bookings.length} of {bookingsTotal} bookings for this range.
              Narrow the date range for a complete view.
            </div>
          )}

          {/* Search and Filter - Compact on mobile */}
          <div className="flex flex-wrap gap-2 mb-3 sm:mb-6">
            <div className="flex-1 min-w-[150px] relative">
              <Search className="absolute left-2 top-2.5 h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-7 sm:pl-10 h-9 text-sm"
              />
            </div>
            <select
              className="px-2 sm:px-3 py-1.5 border rounded-md text-xs sm:text-sm bg-white"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              className="px-2 sm:px-3 py-1.5 border rounded-md text-xs sm:text-sm bg-white"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <option value="all">All Sources</option>
              <option value="direct">Direct</option>
              <option value="online">Online</option>
              <option value="playo">Playo</option>
              <option value="hudle">Hudle</option>
            </select>
            <select
              className="px-2 sm:px-3 py-1.5 border rounded-md text-xs sm:text-sm bg-white"
              value={bookingTypeFilter}
              onChange={(e) => setBookingTypeFilter(e.target.value)}
              data-testid="booking-type-filter"
            >
              <option value="all">All Types</option>
              <option value="b2b">B2B</option>
              <option value="b2c">B2C</option>
              <option value="event">Event</option>
            </select>
            <select
              className="px-2 sm:px-3 py-1.5 border rounded-md text-xs sm:text-sm bg-white"
              value={timeCategory}
              onChange={(e) => setTimeCategory(e.target.value)}
              data-testid="time-category-filter"
            >
              <option value="all">All Hours</option>
              <option value="peak">Peak Hours</option>
              <option value="non-peak">Non-Peak</option>
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Loading...</span>
            </div>
          ) : (
            <>
              {/* Bulk-cancel action bar (visible when ≥1 selected) */}
              {selectedIds.size > 0 && (
                <div
                  className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
                  data-testid="bulk-action-bar"
                >
                  <div className="text-sm text-red-900 font-medium">
                    {selectedIds.size} booking{selectedIds.size !== 1 ? 's' : ''} selected
                    {selectedIds.size >= MAX_BULK && (
                      <span className="ml-2 text-xs text-red-600">(max reached)</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline" size="sm"
                      onClick={clearSelection}
                      data-testid="bulk-clear-selection"
                    >
                      Clear
                    </Button>
                    {canEditBookings && (
                    <Button
                      size="sm"
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => setBulkCancelOpen(true)}
                      data-testid="bulk-cancel-trigger"
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1.5" />
                      Cancel Selected
                    </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Desktop Table - Hidden on mobile */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 w-10">
                        <button
                          type="button"
                          onClick={toggleAllVisible}
                          disabled={selectableBookings.length === 0}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                          title={allVisibleSelected ? 'Deselect all' : 'Select all'}
                          data-testid="bulk-select-all"
                          aria-label="Select all"
                        >
                          {allVisibleSelected
                            ? <CheckSquare className="h-4 w-4 text-blue-600" />
                            : <Square className="h-4 w-4 text-gray-400" />}
                        </button>
                      </th>
                      <th className="text-left p-3 text-sm font-semibold">ID</th>
                      <th className="text-left p-3 text-sm font-semibold">Date & Time</th>
                      <th className="text-left p-3 text-sm font-semibold">Customer</th>
                      <th className="text-left p-3 text-sm font-semibold">Contact</th>
                      <th className="text-left p-3 text-sm font-semibold">Slot</th>
                      <th className="text-left p-3 text-sm font-semibold">Source</th>
                      <th className="text-left p-3 text-sm font-semibold">Type</th>
                      <th className="text-left p-3 text-sm font-semibold">Amount</th>
                      <th className="text-left p-3 text-sm font-semibold">Status</th>
                      <th className="text-left p-3 text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="text-center py-8 text-gray-500">
                          No bookings found
                        </td>
                      </tr>
                    ) : (
                      filteredBookings.map((booking) => {
                        const customerName = booking.customer_name || booking.customerName || '';
                        const customerPhone = booking.customer_phone || booking.customerPhone || '';
                        const customerEmail = booking.customer_email || booking.customerEmail || '';
                        const startTime = booking.start_time || booking.startTime || '';
                        const endTime = booking.end_time || booking.endTime || '';
                        const slotType = booking.slot_type || booking.slotType || '';
                        const source = booking.source || 'direct';
                        const bookingType = booking.booking_type || booking.bookingType || 'b2c';
                        const isConsolidated = isConsolidatedPlayoBooking(booking);
                        const isExpanded = expandedPlayoBookings[booking.id];
                        const originalBookings = isConsolidated ? getOriginalBookings(booking) : [];
                        
                        // Source badge colors
                        const sourceColors = {
                          direct: 'bg-gray-100 text-gray-700',
                          online: 'bg-blue-100 text-blue-700',
                          playo: 'bg-green-100 text-green-700',
                          hudle: 'bg-purple-100 text-purple-700'
                        };
                        
                        // Type badge colors
                        const typeColors = {
                          b2c: 'bg-blue-100 text-blue-700',
                          b2b: 'bg-orange-100 text-orange-700',
                          event: 'bg-pink-100 text-pink-700'
                        };
                        
                        return (
                          <React.Fragment key={booking.id}>
                            <tr className={`border-b hover:bg-gray-50 ${isConsolidated ? 'bg-green-50' : ''} ${selectedIds.has(booking.id) ? 'bg-blue-50' : ''}`}>
                              <td className="p-3 w-10">
                                {(booking.status === 'cancelled' || isConsolidated) ? (
                                  <Square className="h-4 w-4 text-gray-200" />
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => toggleOne(booking.id)}
                                    className="p-1 -m-1 rounded hover:bg-gray-100"
                                    data-testid={`booking-checkbox-${booking.id}`}
                                    aria-label={selectedIds.has(booking.id) ? 'Deselect' : 'Select'}
                                  >
                                    {selectedIds.has(booking.id)
                                      ? <CheckSquare className="h-4 w-4 text-blue-600" />
                                      : <Square className="h-4 w-4 text-gray-400" />}
                                  </button>
                                )}
                              </td>
                              <td className="p-3 text-sm font-mono">
                                {isConsolidated ? (
                                  <div className="flex items-center gap-1">
                                    <Layers className="h-3 w-3 text-green-600" />
                                    <span className="text-green-700">{booking.slotCount} slots</span>
                                  </div>
                                ) : (
                                  <span>{booking.id?.slice(0, 8)}...</span>
                                )}
                              </td>
                              <td className="p-3 text-sm">
                                <div>{formatDate(booking.date)}</div>
                                <div className="text-gray-500">
                                  {formatTime(startTime)} - {formatTime(endTime)}
                                  {isConsolidated && (
                                    <span className="ml-1 text-green-600 font-medium">
                                      ({formatDuration(booking.duration)})
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-3 text-sm">
                                <div className="font-semibold">{customerName}</div>
                              </td>
                              <td className="p-3 text-sm">
                                <div className="text-gray-600">{customerPhone}</div>
                                <div className="text-gray-500 text-xs">{customerEmail}</div>
                              </td>
                              <td className="p-3 text-sm">
                                {booking.court_name ? (
                                  <span data-testid={`booking-court-${booking.id}`}>{booking.court_name}</span>
                                ) : (
                                  <>
                                    <span className="capitalize">{slotType}</span>
                                    {booking.section && booking.section !== 'full' && (
                                      <div className="text-xs text-gray-500">({booking.section})</div>
                                    )}
                                  </>
                                )}
                              </td>
                              <td className="p-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${sourceColors[source] || 'bg-gray-100 text-gray-700'}`}>
                                  {source}
                                  {isConsolidated && <span className="ml-1">✓</span>}
                                </span>
                              </td>
                              <td className="p-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium uppercase ${typeColors[bookingType] || 'bg-gray-100 text-gray-700'}`}>
                                  {bookingType}
                                </span>
                              </td>
                              <td className="p-3 text-sm font-semibold">
                                ₹{booking.amount}
                                {isConsolidated && (
                                  <div className="text-xs text-gray-500 font-normal">
                                    (₹{Math.round(booking.amount / booking.slotCount)}/slot)
                                  </div>
                                )}
                              </td>
                              <td className="p-3 text-sm">
                                <span
                                  className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                                    booking.status === 'confirmed'
                                      ? 'bg-blue-100 text-blue-700'
                                      : booking.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {booking.status?.toUpperCase()}
                                </span>
                                {booking.status === 'cancelled' && (booking.refund_amount > 0) && (
                                  <div
                                    className="mt-1 text-[11px] font-medium text-orange-600"
                                    data-testid={`booking-refund-badge-${booking.id}`}
                                    title={booking.refund_processed_at ? `Refunded on ${new Date(booking.refund_processed_at).toLocaleString('en-IN')}` : 'Refunded'}
                                  >
                                    Refunded ₹{booking.refund_amount}
                                    {booking.refund_status && booking.refund_status !== 'processed' ? ` (${booking.refund_status})` : ''}
                                  </div>
                                )}
                              </td>
                              <td className="p-3 text-sm">
                                <div className="flex space-x-2">
                                  {!isConsolidated && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setViewBooking(booking)}
                                      title="View details"
                                      data-testid={`booking-view-${booking.id}`}
                                    >
                                      <Eye className="h-3 w-3" />
                                    </Button>
                                  )}
                                  {isConsolidated && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => togglePlayoExpansion(booking.id)}
                                      title="View slot breakdown"
                                    >
                                      {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                    </Button>
                                  )}
                                  {!isConsolidated && canEditBookings && booking.status === 'pending' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleConfirm(booking.id)}
                                    >
                                      <CheckCircle className="h-3 w-3" />
                                    </Button>
                                  )}
                                  {!isConsolidated && canEditBookings && booking.status !== 'cancelled' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleCancel(booking.id)}
                                    >
                                      <XCircle className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {/* Expanded Playo slot breakdown */}
                            {isConsolidated && isExpanded && originalBookings.map((slot, idx) => (
                              <tr key={slot.id} className="bg-green-25 border-b border-green-100">
                                <td className="p-2 w-10"></td>
                                <td className="p-2 pl-8 text-xs text-gray-500">
                                  └ Slot {idx + 1}
                                </td>
                                <td className="p-2 text-xs text-gray-600">
                                  {formatTime(slot.start_time || slot.startTime)} - {formatTime(slot.end_time || slot.endTime)}
                                </td>
                                <td colSpan="4" className="p-2 text-xs text-gray-500">
                                  ID: {slot.id?.slice(0, 12)}...
                                </td>
                                <td className="p-2 text-xs text-gray-600">₹{slot.amount}</td>
                                <td className="p-2 text-xs">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                    slot.status === 'confirmed' ? 'bg-blue-100 text-blue-600' : 
                                    slot.status === 'pending' ? 'bg-yellow-100 text-yellow-600' : 
                                    'bg-red-100 text-red-600'
                                  }`}>
                                    {slot.status}
                                  </span>
                                </td>
                                <td className="p-2 text-xs">
                                  {canEditBookings && slot.status !== 'cancelled' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0"
                                      onClick={() => handleCancel(slot.id)}
                                      title="Cancel this slot"
                                    >
                                      <XCircle className="h-3 w-3 text-red-500" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card List */}
              <div className="sm:hidden space-y-2">
                {filteredBookings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No bookings found
                  </div>
                ) : (
                  filteredBookings.map((booking) => {
                    const customerName = booking.customer_name || booking.customerName || '';
                    const customerPhone = booking.customer_phone || booking.customerPhone || '';
                    const startTime = booking.start_time || booking.startTime || '';
                    const endTime = booking.end_time || booking.endTime || '';
                    const slotType = booking.slot_type || booking.slotType || '';
                    const source = booking.source || 'direct';
                    const bookingType = booking.booking_type || booking.bookingType || 'b2c';
                    const isConsolidated = isConsolidatedPlayoBooking(booking);
                    const isExpanded = expandedPlayoBookings[booking.id];
                    const originalBookings = isConsolidated ? getOriginalBookings(booking) : [];
                    
                    // Source badge colors
                    const sourceColors = {
                      direct: 'bg-gray-200 text-gray-700',
                      online: 'bg-blue-200 text-blue-700',
                      playo: 'bg-green-200 text-green-700',
                      hudle: 'bg-purple-200 text-purple-700'
                    };
                    
                    return (
                      <div key={booking.id}>
                        <div 
                          className={`p-2.5 rounded-lg border ${
                            isConsolidated ? 'bg-green-50 border-green-300' :
                            booking.status === 'confirmed' ? 'bg-blue-50 border-blue-200' :
                            booking.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
                            'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            {/* Left side - Main info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {isConsolidated && (
                                  <Layers className="h-3.5 w-3.5 text-green-600 shrink-0" />
                                )}
                                <span className="font-semibold text-sm truncate">{customerName}</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                  booking.status === 'confirmed' ? 'bg-blue-200 text-blue-800' :
                                  booking.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                                  'bg-red-200 text-red-800'
                                }`}>
                                  {booking.status?.slice(0, 4).toUpperCase()}
                                </span>
                                {booking.status === 'cancelled' && (booking.refund_amount > 0) && (
                                  <span
                                    className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700"
                                    data-testid={`booking-refund-badge-m-${booking.id}`}
                                  >
                                    ↩ ₹{booking.refund_amount}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-600 mt-0.5">
                                {formatDateCompact(booking.date)} • {formatTimeCompact(startTime)}-{formatTimeCompact(endTime)}
                                {isConsolidated && (
                                  <span className="ml-1 text-green-600 font-medium">
                                    ({formatDuration(booking.duration)})
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-xs">
                                <span className="text-gray-500">{customerPhone}</span>
                                <span className="font-semibold text-gray-700">₹{booking.amount}</span>
                                {isConsolidated && (
                                  <span className="text-green-600 text-[10px]">
                                    {booking.slotCount} slots
                                  </span>
                                )}
                                {!isConsolidated && (
                                  <span className="text-gray-400">{booking.court_name || slotType}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 mt-1">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${sourceColors[source]}`}>
                                  {source}
                                  {isConsolidated && ' ✓'}
                                </span>
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase bg-gray-200 text-gray-700">
                                  {bookingType}
                                </span>
                              </div>
                            </div>
                            
                            {/* Right side - Actions */}
                            <div className="flex gap-1 shrink-0">
                              {!isConsolidated && (
                                <button
                                  onClick={() => setViewBooking(booking)}
                                  className="p-1.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                                  title="View details"
                                  data-testid={`booking-view-m-${booking.id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              )}
                              {isConsolidated && (
                                <button
                                  onClick={() => togglePlayoExpansion(booking.id)}
                                  className="p-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200"
                                  title="View slot breakdown"
                                >
                                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </button>
                              )}
                              {!isConsolidated && canEditBookings && booking.status === 'pending' && (
                                <button
                                  onClick={() => handleConfirm(booking.id)}
                                  className="p-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                              )}
                              {!isConsolidated && canEditBookings && booking.status !== 'cancelled' && (
                                <button
                                  onClick={() => handleCancel(booking.id)}
                                  className="p-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200"
                                >
                                  <XCircle className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Expanded slot breakdown for mobile */}
                        {isConsolidated && isExpanded && (
                          <div className="ml-4 mt-1 space-y-1">
                            {originalBookings.map((slot, idx) => (
                              <div 
                                key={slot.id} 
                                className="flex items-center justify-between p-1.5 bg-green-25 border border-green-100 rounded text-xs"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-400">└</span>
                                  <span className="text-gray-600">
                                    {formatTimeCompact(slot.start_time || slot.startTime)}-{formatTimeCompact(slot.end_time || slot.endTime)}
                                  </span>
                                  <span className="text-gray-500">₹{slot.amount}</span>
                                </div>
                                {canEditBookings && slot.status !== 'cancelled' && (
                                  <button
                                    onClick={() => handleCancel(slot.id)}
                                    className="p-1 rounded hover:bg-red-100"
                                    title="Cancel this slot"
                                  >
                                    <XCircle className="h-3 w-3 text-red-500" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <BulkCancelModal
        open={bulkCancelOpen}
        bookings={selectedBookings}
        onClose={() => setBulkCancelOpen(false)}
        onComplete={onBulkComplete}
      />
      {viewBooking && (
        <BookingDialog
          isOpen={!!viewBooking}
          onClose={() => setViewBooking(null)}
          slotInfo={{ booking: viewBooking, date: viewBooking.date, status: viewBooking.status }}
        />
      )}
      </>
      )}
    </div>
  );
};

export default BookingManagement;
